import { supabase } from '@/lib/supabaseClient';
import {
  fetchSettingsByKey,
  upsertSettingsByKey,
} from '@/lib/settingsData';
import { formatUsd, parseContractorDate, deriveInvoiceStatus } from '@/lib/jobsData';
import * as pqi from '@/lib/pqiTracker';
import { hydrateEstimate } from '@/lib/estimatesData';
import {
  uploadPortalPhoto,
  deletePortalPhoto,
  isStorageUrl,
  resolvePortalPhotos,
} from '@/lib/portalStorage';

export { formatUsd };

/* ----------------------------------------------------------------------------
 * Settings keys
 * ------------------------------------------------------------------------- */
export const CLIENT_PORTAL_KEYS = {
  PORTALS: 'client_portals',
  MESSAGES: 'client_portal_messages',
  SIGNATURES: 'client_portal_signatures',
  PHOTOS: 'client_portal_shared_photos',
  ALERTS: 'client_portal_signature_alerts',
};

/* ----------------------------------------------------------------------------
 * Code generation
 * ------------------------------------------------------------------------- */
// New client codes are 6-char alphanumeric from an unambiguous alphabet
// (no 0/O, 1/I/L, so they're easy to read aloud and text). ~31^6 ≈ 890M space —
// shorter than the old 8-digit code yet a larger keyspace than the 100M numeric
// one, still backed by the per-IP lockout in sql/019. Legacy numeric codes (4–8
// digit) keep working until rotated; the portal input accepts alphanumeric.
const CLIENT_CODE_LEN = 6;
const CODE_ALPHABET = 'ABCDEFGHJKMNPQRSTUVWXYZ23456789';

/**
 * Cryptographically-strong code generator (rejection-sampled to avoid modulo
 * bias). Draws from CODE_ALPHABET.
 */
const genClientCode = (len = CLIENT_CODE_LEN) => {
  const c =
    typeof crypto !== 'undefined' && crypto.getRandomValues ? crypto : null;
  const n = CODE_ALPHABET.length;
  let out = '';
  if (c) {
    const max = Math.floor(256 / n) * n; // reject the tail for uniform sampling
    while (out.length < len) {
      const buf = new Uint8Array(len);
      c.getRandomValues(buf);
      for (let i = 0; i < buf.length && out.length < len; i++) {
        if (buf[i] < max) out += CODE_ALPHABET[buf[i] % n];
      }
    }
  } else {
    for (let i = 0; i < len; i++) {
      out += CODE_ALPHABET[Math.floor(Math.random() * n)];
    }
  }
  return out;
};

const genId = (prefix) => {
  const id =
    typeof crypto !== 'undefined' && crypto.randomUUID
      ? crypto.randomUUID().split('-')[0].toUpperCase()
      : Math.random().toString(36).slice(2, 8).toUpperCase();
  return `${prefix}-${id}`;
};

/* ----------------------------------------------------------------------------
 * Loaders
 * ------------------------------------------------------------------------- */
export const fetchClientsForPortal = async () => {
  const { data, error } = await supabase
    .from('clients')
    .select('id,name,type,phone,email,address,created_at')
    .order('name', { ascending: true });
  if (error) throw error;
  return data || [];
};

export const fetchJobsForPortal = async () => {
  const { data, error } = await supabase
    .from('jobs')
    .select(
      'id,name,client,client_id,address,contract,co_total,status,phase,invoices,notes,created_at'
    )
    .order('created_at', { ascending: false });
  if (error) throw error;
  return data || [];
};

export const fetchEstimatesForPortal = async () => {
  // Fetch full estimate rows and hydrate them so the Proposal tab has access
  // to sections, line items, project overview, payment schedule, etc. \u2014 not
  // just the headline total. The notes column carries the JSON blob payload
  // (sections, overview, payment_schedule, tax_rate). We tolerate failures
  // gracefully \u2014 the Proposal tab will simply show an empty state.
  try {
    const { data, error } = await supabase
      .from('estimates')
      .select(
        'id,job_id,client_id,title,status,subtotal,tax,total,notes,created_at'
      )
      .order('created_at', { ascending: false });
    if (error) return [];
    return (data || [])
      .map((row) => {
        try {
          return hydrateEstimate(row);
        } catch (_) {
          // Hydration shouldn't fail, but if a row's notes blob is malformed
          // we still want the rest of the list to render.
          return {
            id: row?.id,
            job_id: row?.job_id || null,
            client_id: row?.client_id || null,
            title: row?.title || '',
            project_name: row?.title || '',
            status: row?.status || 'draft',
            subtotal: Number(row?.subtotal) || 0,
            tax: Number(row?.tax) || 0,
            total: Number(row?.total) || 0,
            sections: [],
            overview: {},
            payment_schedule: [],
            tax_rate: 0,
            created_at: row?.created_at || null,
          };
        }
      })
      .filter(Boolean);
  } catch (e) {
    return [];
  }
};

export const fetchPortalConfig = async () =>
  (await fetchSettingsByKey(CLIENT_PORTAL_KEYS.PORTALS, {})).value || {};

export const fetchPortalMessages = async () =>
  (await fetchSettingsByKey(CLIENT_PORTAL_KEYS.MESSAGES, {})).value || {};

export const fetchPortalSignatures = async () =>
  (await fetchSettingsByKey(CLIENT_PORTAL_KEYS.SIGNATURES, {})).value || {};

export const fetchPortalPhotos = async () =>
  (await fetchSettingsByKey(CLIENT_PORTAL_KEYS.PHOTOS, {})).value || {};

export const fetchPortalAlerts = async () =>
  (await fetchSettingsByKey(CLIENT_PORTAL_KEYS.ALERTS, [])).value || [];

/**
 * Load everything in one go. Used by contractor-side dashboard.
 */
export const loadAllClientPortalData = async () => {
  const [clients, jobs, estimates, portals, messages, signatures, photos, alerts] =
    await Promise.all([
      fetchClientsForPortal(),
      fetchJobsForPortal(),
      fetchEstimatesForPortal(),
      fetchPortalConfig(),
      fetchPortalMessages(),
      fetchPortalSignatures(),
      fetchPortalPhotos(),
      fetchPortalAlerts(),
    ]);
  return { clients, jobs, estimates, portals, messages, signatures, photos, alerts };
};

/* ----------------------------------------------------------------------------
 * Public-portal RPC adapter
 *   Prefers Postgres RPCs (security-definer, code-validated, anon-callable).
 *   Falls back to direct table reads when RPCs are unavailable (e.g. before
 *   migration 002_portal_rpcs.sql has been applied) and a Supabase auth
 *   session is present.
 * ------------------------------------------------------------------------- */
const _rpcUnavailable = (error) => {
  if (!error) return false;
  const code = error.code || '';
  const msg = (error.message || '').toLowerCase();
  // 42883 = function does not exist; 42501 = permission denied
  return (
    code === '42883' ||
    code === '42501' ||
    code === 'PGRST202' || // PostgREST function not found
    msg.includes('does not exist') ||
    msg.includes('not found in the schema') ||
    msg.includes('permission denied')
  );
};

/**
 * Authenticate a portal code and return the full payload using the public
 * RPC (preferred) or fall back to client-side composition.
 */
export const loadPortalByCode = async (code) => {
  // Try RPC first
  try {
    const { data, error } = await supabase.rpc('client_portal_load', { p_code: code });
    if (!error && data && data.ok !== false) {
      return {
        ok: true,
        source: 'rpc',
        config: data.config || null,
        // White-label branding (migration 025) — client-safe brand fields only.
        company: data.company || null,
        jobs: Array.isArray(data.jobs) ? data.jobs : [],
        // Hydrate estimates so sections/overview/payment_schedule/tax_rate
        // (which live inside the `notes` JSON blob) are available to the
        // ProposalTab. Without this the Proposal would only ever see the
        // headline total/subtotal/tax columns and miss the actual scope.
        estimates: Array.isArray(data.estimates)
          ? data.estimates.map((row) => {
              try {
                return hydrateEstimate(row);
              } catch (_) {
                return row;
              }
            })
          : [],
        messages: Array.isArray(data.messages) ? data.messages : [],
        // Resolve signed URLs when the bucket is private (sql/021 cutover);
        // no-op otherwise.
        photos: await resolvePortalPhotos(
          code,
          'client',
          Array.isArray(data.photos) ? data.photos : []
        ),
        signatures: Array.isArray(data.signatures) ? data.signatures : [],
      };
    }
    if (data?.ok === false) {
      return { ok: false, error: data.error || 'invalid_code' };
    }
    // Any error here is terminal. We deliberately do NOT fall back to reading
    // `jobs`/`estimates` directly: that path ships the full `estimates.notes`
    // blob (raw line-item rates, incl. Bottom-Line-masked ones) and internal
    // `jobs.notes` to the client, bypassing the server-side sanitization in
    // sql/006_portal_rpc_hardening.sql. If the RPC is missing, the portal is
    // simply unavailable until migrations 002/006 are applied.
    return {
      ok: false,
      error: _rpcUnavailable(error) ? 'portal_unavailable' : error?.message || 'rpc_failed',
    };
  } catch (e) {
    return { ok: false, error: e?.message || 'rpc_failed' };
  }
};

export const sendPortalMessage = async (code, body) => {
  // RPC first
  try {
    const { data, error } = await supabase.rpc('client_portal_send_message', {
      p_code: code,
      p_body: body,
    });
    if (!error && data?.ok) return { ok: true, source: 'rpc', message: data.message };
    if (data?.ok === false) return { ok: false, error: data.error };
    if (!_rpcUnavailable(error)) return { ok: false, error: error?.message || 'rpc_failed' };
  } catch (e) { /* fall through */ }

  // Fallback to direct write via existing helper
  try {
    const portals = await fetchPortalConfig();
    const entry = findPortalByCode(code, portals);
    if (!entry) return { ok: false, error: 'invalid_code' };
    const messages = await fetchPortalMessages();
    const next = await sendMessage(
      { clientId: entry.client_id, from: 'client', body },
      messages
    );
    const arr = next[entry.client_id] || [];
    return { ok: true, source: 'direct', message: arr[arr.length - 1] };
  } catch (e) {
    return { ok: false, error: e?.message || 'write_failed' };
  }
};

/* ----------------------------------------------------------------------------
 * Signature image upload
 *
 * The signing RPCs (023/033/038) REJECT inline `data:` signatures — a drawn
 * signature must be uploaded to the portal-photos bucket first and only its
 * PATH passed (`client/<client_id>/signatures/...`, which the portal-photo-url
 * edge function will sign for display under that portal's own prefix). The
 * frontend was never taught this, so every drawn signature came back as
 * `inline_signature_rejected`. If the upload fails we sign with name only —
 * a $26K acceptance must never be blocked by an image upload.
 * ------------------------------------------------------------------------- */
const signatureDataUrlToFile = (dataUrl) => {
  const m = String(dataUrl || '').match(/^data:(image\/[a-z+.-]+);base64,(.+)$/i);
  if (!m) return null;
  try {
    const bin = atob(m[2]);
    const bytes = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i += 1) bytes[i] = bin.charCodeAt(i);
    return new File([bytes], `signature-${Date.now()}.png`, { type: m[1] });
  } catch {
    return null;
  }
};

export const uploadSignatureImage = async (dataUrl, clientId) => {
  const file = signatureDataUrlToFile(dataUrl);
  if (!file) return { ok: false, error: 'bad_signature' };
  const res = await uploadPortalPhoto(file, {
    scope: 'client',
    ownerId: clientId || 'anon',
    subpath: 'signatures',
  });
  if (!res.ok) return res;
  return { ok: true, path: res.path };
};

// Normalize whatever the UI hands us into an RPC-safe signature value:
// data URL → uploaded storage path; anything already path/URL passes through.
const prepareSignatureValue = async (raw, clientId) => {
  if (!raw) return null;
  if (String(raw).startsWith('data:')) {
    const up = await uploadSignatureImage(raw, clientId);
    return up.ok ? up.path : null; // never block signing on an upload failure
  }
  return String(raw).slice(0, 2048);
};

export const recordPortalSignature = async (code, payload) => {
  const sigValue = await prepareSignatureValue(
    payload.signatureDataUrl,
    payload.clientId
  );
  // RPC first
  try {
    const { data, error } = await supabase.rpc('client_portal_record_signature', {
      p_code: code,
      p_estimate_id: payload.estimateId || null,
      p_estimate_title: payload.estimateTitle || '',
      p_total: Number(payload.total) || 0,
      p_signed_name: payload.signedName,
      p_signature_url: sigValue,
      p_job_id: payload.jobId || null,
    });
    if (!error && data?.ok) return { ok: true, source: 'rpc', signature: data.signature };
    if (data?.ok === false) return { ok: false, error: data.error };
    if (!_rpcUnavailable(error)) return { ok: false, error: error?.message || 'rpc_failed' };
  } catch (e) { /* fall through */ }

  // Fallback to direct settings writes
  try {
    const portals = await fetchPortalConfig();
    const entry = findPortalByCode(code, portals);
    if (!entry) return { ok: false, error: 'invalid_code' };
    const current = await fetchPortalSignatures();
    const next = await recordSignature(
      { ...payload, signatureDataUrl: sigValue, clientId: entry.client_id },
      current
    );
    const arr = next[entry.client_id] || [];
    return { ok: true, source: 'direct', signature: arr[arr.length - 1] };
  } catch (e) {
    return { ok: false, error: e?.message || 'write_failed' };
  }
};

/**
 * Client signs (approves) a pending change order from their portal. RPC-only:
 * approving a CO writes public.jobs, which anonymous portal users cannot do
 * directly after the RLS lockdown — client_portal_sign_change_order (033) is
 * SECURITY DEFINER and enforces code + ownership itself.
 */
export const signPortalChangeOrder = async (
  code,
  { jobId, coId, signedName, signatureUrl, clientId } = {}
) => {
  try {
    const sigValue = await prepareSignatureValue(signatureUrl, clientId);
    const { data, error } = await supabase.rpc('client_portal_sign_change_order', {
      p_code: code,
      p_job_id: jobId,
      p_co_id: coId,
      p_signed_name: signedName,
      p_signature_url: sigValue,
    });
    if (!error && data?.ok) return { ok: true, co_total: data.co_total };
    if (data?.ok === false) return { ok: false, error: data.error };
    return { ok: false, error: error?.message || 'rpc_failed' };
  } catch (e) {
    return { ok: false, error: e?.message || 'sign_failed' };
  }
};

export const markPortalRead = async (code) => {
  try {
    const { data, error } = await supabase.rpc('client_portal_mark_read', { p_code: code });
    if (!error && data?.ok) return { ok: true, source: 'rpc' };
    if (!_rpcUnavailable(error)) return { ok: false, error: error?.message };
  } catch (e) { /* fall through */ }
  try {
    const portals = await fetchPortalConfig();
    const entry = findPortalByCode(code, portals);
    if (!entry) return { ok: false, error: 'invalid_code' };
    const messages = await fetchPortalMessages();
    await markMessagesRead(entry.client_id, 'contractor', messages);
    return { ok: true, source: 'direct' };
  } catch (e) {
    return { ok: false, error: e?.message };
  }
};

/* ----------------------------------------------------------------------------
 * Code management
 * ------------------------------------------------------------------------- */

/** Ensure each client has a portal entry with a unique 4-digit code. */
export const ensureCodesForClients = async (clients = [], existing = {}) => {
  const next = { ...existing };
  const used = new Set(
    Object.values(next)
      .map((v) => v?.code)
      .filter(Boolean)
  );
  let mutated = false;
  for (const c of clients) {
    if (!c?.id) continue;
    if (next[c.id]?.code) continue;
    let code;
    let attempts = 0;
    do {
      code = genClientCode();
      attempts += 1;
      if (attempts > 50) break;
    } while (used.has(code));
    used.add(code);
    next[c.id] = {
      code,
      client_id: c.id,
      client_name: c.name,
      type: c.type || 'client',
      active: true,
      created_at: new Date().toISOString(),
    };
    mutated = true;
  }
  if (mutated) await upsertSettingsByKey(CLIENT_PORTAL_KEYS.PORTALS, next);
  return next;
};

export const regenerateCode = async (clientId, portals = {}) => {
  if (!clientId) throw new Error('Missing client id');
  const used = new Set(
    Object.values(portals)
      .map((v) => v?.code)
      .filter(Boolean)
  );
  let code;
  do {
    code = genClientCode();
  } while (used.has(code));
  const next = {
    ...portals,
    [clientId]: {
      ...(portals[clientId] || {}),
      code,
      client_id: clientId,
      created_at: new Date().toISOString(),
      active: true,
    },
  };
  await upsertSettingsByKey(CLIENT_PORTAL_KEYS.PORTALS, next);
  return next;
};

export const setPortalActive = async (clientId, active, portals = {}) => {
  const next = {
    ...portals,
    [clientId]: {
      ...(portals[clientId] || {}),
      client_id: clientId,
      active: !!active,
    },
  };
  await upsertSettingsByKey(CLIENT_PORTAL_KEYS.PORTALS, next);
  return next;
};

/** Reverse-lookup a portal entry by code. Returns null if not found / inactive. */
export const findPortalByCode = (code, portals = {}) => {
  if (!code) return null;
  const target = String(code).trim();
  for (const entry of Object.values(portals || {})) {
    if (entry?.active && String(entry?.code) === target) return entry;
  }
  return null;
};

export const buildPortalLink = (code) => {
  if (typeof window === 'undefined') return `/portal/client/${code}`;
  return `${window.location.origin}/portal/client/${code}`;
};

/* ----------------------------------------------------------------------------
 * Messages
 * ------------------------------------------------------------------------- */
export const sendMessage = async ({ clientId, from, body }, messages = {}) => {
  if (!clientId) throw new Error('Missing client id');
  if (!body?.trim()) throw new Error('Message body required');
  const entry = {
    id: genId('MSG'),
    from: from === 'client' ? 'client' : 'contractor',
    body: body.trim(),
    sent_at: new Date().toISOString(),
    read: false,
  };
  const next = {
    ...messages,
    [clientId]: [...(messages[clientId] || []), entry],
  };
  await upsertSettingsByKey(CLIENT_PORTAL_KEYS.MESSAGES, next);
  return next;
};

export const markMessagesRead = async (clientId, by, messages = {}) => {
  if (!clientId) return messages;
  const arr = (messages[clientId] || []).map((m) =>
    m.from !== by ? { ...m, read: true } : m
  );
  const next = { ...messages, [clientId]: arr };
  await upsertSettingsByKey(CLIENT_PORTAL_KEYS.MESSAGES, next);
  return next;
};

export const flattenInbox = (messages = {}, portals = {}) => {
  const rows = [];
  for (const [clientId, arr] of Object.entries(messages || {})) {
    const config = portals?.[clientId];
    if (!Array.isArray(arr) || arr.length === 0) continue;
    const last = arr[arr.length - 1];
    const unread = arr.filter((m) => m.from === 'client' && !m.read).length;
    rows.push({
      client_id: clientId,
      client_name: config?.client_name || 'Unknown client',
      code: config?.code,
      total: arr.length,
      unread,
      last_body: last?.body || '',
      last_at: last?.sent_at || '',
      last_from: last?.from || '',
      messages: arr,
    });
  }
  rows.sort((a, b) => (b.last_at || '').localeCompare(a.last_at || ''));
  return rows;
};

/* ----------------------------------------------------------------------------
 * Signatures
 * ------------------------------------------------------------------------- */
export const recordSignature = async ({
  clientId,
  estimateId,
  signedName,
  signatureDataUrl,
  jobId,
  estimateTitle,
  total,
}, current = {}) => {
  if (!clientId) throw new Error('Missing client id');
  if (!signedName) throw new Error('Signed name required');
  const entry = {
    id: genId('SIG'),
    estimate_id: estimateId || null,
    job_id: jobId || null,
    estimate_title: estimateTitle || '',
    total: Number(total) || 0,
    signed_name: signedName,
    signature_url: signatureDataUrl || null,
    signed_at: new Date().toISOString(),
  };
  const next = {
    ...current,
    [clientId]: [...(current[clientId] || []), entry],
  };
  await upsertSettingsByKey(CLIENT_PORTAL_KEYS.SIGNATURES, next);

  // Also append to alerts so contractor activity feed picks it up.
  const alerts = (await fetchSettingsByKey(CLIENT_PORTAL_KEYS.ALERTS, [])).value || [];
  const nextAlerts = [
    {
      id: entry.id,
      kind: 'signature',
      client_id: clientId,
      job_id: jobId || null,
      estimate_id: estimateId || null,
      estimate_title: estimateTitle || '',
      signed_name: signedName,
      total: Number(total) || 0,
      created_at: entry.signed_at,
    },
    ...alerts,
  ].slice(0, 200);
  await upsertSettingsByKey(CLIENT_PORTAL_KEYS.ALERTS, nextAlerts);

  // PQI: track estimate signed (silent) — this is a "won_job" signal.
  pqi.trackEstimateSigned({
    estimateId,
    clientId,
    jobId: jobId || null,
    total,
    signedName,
    estimateTitle,
  });

  return next;
};

/* ----------------------------------------------------------------------------
 * Shared Photos
 *
 * Modern flow: caller passes `files` (File[]) — we upload them to Supabase
 * Storage (bucket `portal-photos`) and persist only the public URLs in the
 * settings table.
 *
 * Legacy flow: caller passes `photos` array of `{ url, caption }` (data URLs)
 * — we still accept and persist these for backwards compatibility.
 * ------------------------------------------------------------------------- */
export const sharePhotos = async (
  { clientId, files = [], photos = [], caption = '' },
  current = {}
) => {
  if (!clientId) throw new Error('Missing client id');
  const sharedAt = new Date().toISOString();
  const newOnes = [];
  const failures = [];

  // Upload any provided files to Storage (preferred path).
  for (const f of Array.from(files || [])) {
    const result = await uploadPortalPhoto(f, { scope: 'client', ownerId: clientId });
    if (result.ok && result.url) {
      newOnes.push({
        id: genId('PHO'),
        url: result.url,
        path: result.path,
        caption: caption || '',
        shared_at: sharedAt,
      });
    } else {
      failures.push(result.error || 'upload_failed');
    }
  }

  // Pass-through legacy items (already-URL'd or data URL entries).
  for (const p of photos) {
    const url = p?.url || (typeof p === 'string' ? p : null);
    if (!url) continue;
    newOnes.push({
      id: genId('PHO'),
      url,
      caption: p?.caption || caption || '',
      shared_at: sharedAt,
    });
  }

  if (newOnes.length === 0) {
    const reason = failures[0] || 'no_photos';
    throw new Error(reason);
  }

  const next = {
    ...current,
    [clientId]: [...newOnes, ...(current[clientId] || [])],
  };
  await upsertSettingsByKey(CLIENT_PORTAL_KEYS.PHOTOS, next);
  return { next, uploaded: newOnes.length, failed: failures.length };
};

export const removeSharedPhoto = async ({ clientId, photoId }, current = {}) => {
  if (!clientId) throw new Error('Missing client id');
  const arr = current[clientId] || [];
  const target = arr.find((p) => p.id === photoId);
  const filtered = arr.filter((p) => p.id !== photoId);
  const next = { ...current, [clientId]: filtered };
  await upsertSettingsByKey(CLIENT_PORTAL_KEYS.PHOTOS, next);

  // Best-effort cleanup of the underlying Storage object.
  if (target?.url && isStorageUrl(target.url)) {
    deletePortalPhoto(target.url).catch(() => {});
  }
  return next;
};

export const fileToDataUrl = (file) =>
  new Promise((resolve, reject) => {
    if (!file) return resolve(null);
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

/* ----------------------------------------------------------------------------
 * Project summary builder for client-side
 * ------------------------------------------------------------------------- */
export const buildClientProjectSummary = (clientConfig, jobs = [], estimates = []) => {
  if (!clientConfig?.client_id) return null;
  const cid = clientConfig.client_id;
  const cname = (clientConfig.client_name || '').trim().toLowerCase();
  // Match by client_id when present; otherwise fall back to the text `client`
  // column on the job (case-insensitive). Older seeded jobs may not have
  // client_id wired up yet.
  const myJobs = jobs.filter((j) => {
    if (j.client_id && j.client_id === cid) return true;
    if (!j.client_id && cname && (j.client || '').trim().toLowerCase() === cname) return true;
    return false;
  });
  const allInvoices = [];
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  let contractTotal = 0;
  let coTotal = 0;
  let collected = 0;
  let outstanding = 0;
  let overdue = 0;
  for (const j of myJobs) {
    contractTotal += Number(j.contract) || 0;
    coTotal += Number(j.co_total) || 0;
    const invoices = Array.isArray(j.invoices) ? j.invoices : [];
    for (const inv of invoices) {
      const status = deriveInvoiceStatus(inv);
      const due = parseContractorDate(inv?.due);
      const amt = Number(inv?.amount) || 0;
      if (status === 'paid') collected += amt;
      else outstanding += amt;
      if (status === 'overdue') overdue += amt;
      allInvoices.push({
        id: inv?.id,
        job_id: j.id,
        job_name: j.name,
        desc: inv?.desc || '',
        amount: amt,
        due: inv?.due || '',
        due_date: due,
        status,
      });
    }
  }
  // Sort invoices by due date asc (with paid grouped at bottom).
  allInvoices.sort((a, b) => {
    if (a.status === 'paid' && b.status !== 'paid') return 1;
    if (b.status === 'paid' && a.status !== 'paid') return -1;
    if (a.due_date && b.due_date) return a.due_date - b.due_date;
    return 0;
  });
  const myEstimates = (estimates || []).filter((e) => e.client_id === cid);
  return {
    client_id: cid,
    client_name: clientConfig.client_name,
    jobs: myJobs,
    invoices: allInvoices,
    estimates: myEstimates,
    contract: contractTotal,
    co_total: coTotal,
    revenue: contractTotal + coTotal,
    collected,
    outstanding,
    overdue,
  };
};
